/**
 * OnboardingTour — Cluster J.B/J.C (Feb 2026).
 *
 * react-joyride@3.x wrapper. v3 API differs significantly from v2:
 *   • Per-step `disableBeacon` removed → use top-level `options.skipBeacon`.
 *   • `showSkipButton` removed → include `'skip'` in `options.buttons`.
 *   • `disableOverlayClose` removed → set `options.overlayClickAction: false`.
 *   • `disableScrolling` removed → `options.skipScroll` (default false).
 *   • `floaterProps` removed → `floatingOptions`.
 *   • `placement: 'center'` no longer supported → anchor every step to a real DOM target.
 *
 * Six stops mapped to existing testids — see `/app/memory/cluster_J/live_tree_map.md`.
 *
 * Lifecycle:
 *   • Host (AgentPreviewPage) reads `useTourState()`, mounts this with
 *     `run={shouldRun}` when on /chat empty state.
 *   • Joyride emits `status === 'finished' | 'skipped'` → `onFinish`
 *     callback fires `markComplete`. Skip is treated as completion —
 *     re-prompting would be annoying.
 *
 * Stop #4 (recent matters) auto-skip:
 *   • `RecentMattersStrip` returns null when matters.length === 0.
 *   • `targetExists()` filter at step-build time removes stop #4 from
 *     the array when the testid is absent. Joyride waits up to
 *     `targetWaitTimeout: 1000ms` for missing targets, but pre-filtering
 *     is cleaner (no loader flash).
 *
 * RTL:
 *   • `<html dir="rtl">` (set by LanguageContext) flows through to
 *     react-floater for tooltip positioning.
 *   • Skip/Next/Back/Last button copy localised via `locale`.
 *   • Tooltip text-align + Cairo font set in `styles.tooltip`.
 */
import React, { useEffect, useMemo } from 'react';
import { Joyride, EVENTS, STATUS } from 'react-joyride';
import { useLanguage } from '@/contexts/LanguageContext';

const ACCENT = '#d6b27d';

/** Synchronous DOM check for a testid selector. */
function targetExists(selector) {
  if (typeof document === 'undefined') return false;
  try {
    return Boolean(document.querySelector(selector));
  } catch (_e) {
    return false;
  }
}

/**
 * Detect targets that are present in the DOM but clipped above their
 * scroll container (negative `offsetTop`). This happens to the empty-
 * state hero's logo on small viewports: the WelcomeHero uses
 * `justify-center` which centers the column vertically — when the
 * column overflows the viewport, the top content gets pushed above
 * the visible area and the scroll container's `scrollTop` is pinned
 * at 0. react-joyride can't scroll up to it, so the tooltip would
 * render off-screen. The cleanest mitigation is to drop the stop and
 * have the tour open on the next visible step instead.
 */
function targetReachable(selector) {
  if (typeof document === 'undefined') return false;
  const el = document.querySelector(selector);
  if (!el) return false;
  if (el.offsetTop < 0) return false;
  const rect = el.getBoundingClientRect();
  // Tolerate elements just above the fold (Joyride can scroll those in);
  // bail only when the element is dramatically out of reach.
  if (rect.bottom < -50) return false;
  return true;
}

function buildSteps(isArabic) {
  const t = (en, ar) => (isArabic ? ar : en);

  // Sep 2026 (tour cleanup): reduced from 5 steps to the 3 that map to
  // real, always-present UI. The dropped steps (welcome logo, recent
  // matters strip, Herald pill, closing CTA) either anchored to empty
  // space on desktop or vanished when the target didn't render — the
  // spotlight would land on nothing and users saw a floating tooltip.
  const allSteps = [
    {
      // #1 — composer (welcome textarea always mounted for new sessions)
      target: '[data-testid="welcome-textarea"]',
      placement: 'top',
      title: t('Ask anything', 'اطرح أي سؤال'),
      content: t(
        "Ask a legal question or request a draft. Try: 'Draft a mutual NDA'",
        "اطرح سؤالاً قانونياً أو اطلب مسودة. جرّب: 'اصِغ اتفاقية عدم إفصاح متبادلة'",
      ),
    },
    {
      // #2 — Review contract common-task card
      target: '[data-testid="agent-preview-task-review"]',
      placement: 'top',
      title: t('Review a contract', 'راجع عقداً'),
      content: t(
        'Upload a contract and I will flag risks and gaps under Saudi law.',
        'ارفع عقداً وسأحدد المخاطر والفجوات وفق الأنظمة السعودية.',
      ),
      _onboardingStopId: 'review_task',
    },
    {
      // #3 — right-hand document panel (SplitPanelLayout mounts this
      // even before the first draft — the expand button is a stable
      // anchor at every viewport ≥ md).
      target: '[data-testid="split-panel-right"]',
      placement: isArabic ? 'right' : 'left',
      title: t('Your document panel', 'لوحة المستند'),
      content: t(
        'Drafts and reviews open here on the side, next to the chat.',
        'تظهر المسودات والمراجعات هنا بجانب المحادثة.',
      ),
      _onboardingStopId: 'document_panel',
    },
  ];

  return allSteps.filter((step) => {
    // Every remaining anchor is required — skip only if the target
    // literally isn't mounted (defence in depth for future layouts).
    if (step._onboardingStopId) return targetExists(step.target);
    return true;
  });
}

export default function OnboardingTour({ run, onFinish }) {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  // Jul 2026 mobile fix: the tour's spotlight anchors and vertical
  // positioning break at phone widths (blank highlight boxes, tooltips
  // clipped off-screen). A broken tour is worse than none — suppress
  // below 768px; the mobile home screen is self-explanatory. The
  // actual `return null` sits AFTER every hook call (rules-of-hooks).
  // Deliberately does NOT call onFinish — a phone visit must not mark
  // the tour as seen before the user ever gets the desktop experience.
  const [isMobileVp] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );

  const steps = useMemo(() => buildSteps(isArabic), [isArabic, run]);

  // v3 key is `nextWithProgress` with `{current}` / `{total}` placeholders
  // (NOT the v2 `nextLabelWithProgress` / `{step}` shape). When
  // `options.showProgress` is true, react-joyride uses this template
  // instead of `locale.next`. Both keys are still supplied so EN and
  // AR stay coherent whether the host toggles progress on or off.
  const locale = isArabic
    ? {
        back: 'السابق',
        close: 'إغلاق',
        last: 'إنهاء',
        next: 'التالي',
        nextWithProgress: 'التالي ({current} من {total})',
        skip: 'تخطي الجولة',
      }
    : {
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        nextWithProgress: 'Next ({current} of {total})',
        skip: 'Skip tour',
      };

  // Mobile-safe scroll: react-joyride's `scrollToFirstStep` finds the
  // nearest scrollable ancestor and scrolls THAT, but our V2Layout
  // nests the empty-state hero inside a flex column where the actual
  // scroll container is the page wrapper. On 375px viewports the
  // first target (`dashboard-home-logo`) ends up above the visible
  // area, which means Joyride paints the tooltip offscreen (y < 0).
  //
  // Explicitly walk up the first target's parent chain when the tour
  // begins and scroll every scrollable ancestor (plus window) to the
  // target's top edge. Belt-and-braces but cheap — only fires once
  // per `run` toggle.
  useEffect(() => {
    if (!run || steps.length === 0) return;
    const id = window.setTimeout(() => {
      const el = document.querySelector(steps[0].target);
      if (!el) return;
      try {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
      } catch (_e) {
        // Safari < 15.4 doesn't accept 'instant'; fall back to default
        try { el.scrollIntoView({ block: 'center' }); } catch (_e2) { /* ignore */ }
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [run, steps]);

  // v3: the v2 `callback` prop is gone. Listen for `tour:end` via
  // `onEvent` instead; the EventData carries `status` so the
  // FINISHED/SKIPPED branch logic stays the same.
  const handleEvent = (data) => {
    if (!data) return;
    if (data.type === EVENTS.TOUR_END
        && (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)) {
      onFinish?.(data.status);
    }
  };

  // Jul 2026 mobile fix (see isMobileVp above): placed after every
  // hook call so the hook order is identical on all renders.
  if (isMobileVp) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      locale={locale}
      onEvent={handleEvent}
      options={{
        primaryColor: ACCENT,
        backgroundColor: '#FFFFFF',
        textColor: '#1A1A1A',
        arrowColor: '#FFFFFF',
        overlayColor: 'rgba(0, 0, 0, 0.55)',
        zIndex: 9999,
        skipBeacon: true,                          // open tooltip directly, no beacon-click
        showProgress: true,                        // "Next (1 of 6)"
        buttons: ['skip', 'back', 'primary'],      // expose Skip on every step
        overlayClickAction: false,                 // overlay click is inert
        spotlightPadding: 8,
        spotlightRadius: 6,
      }}
      styles={{
        tooltip: {
          fontFamily: isArabic ? 'Cairo, sans-serif' : 'inherit',
          textAlign: isArabic ? 'right' : 'left',
          borderRadius: 8,
          padding: 16,
          maxWidth: 360,
        },
        tooltipTitle: { fontSize: 15, fontWeight: 600, marginBottom: 6 },
        tooltipContent: { fontSize: 13, lineHeight: 1.55, padding: 0 },
        buttonPrimary: {
          background: '#111111', color: '#FFFFFF',
          borderRadius: 6, padding: '6px 14px', fontSize: 13,
        },
        buttonBack: { color: '#555555', fontSize: 13 },
        buttonSkip: { color: '#888888', fontSize: 12 },
      }}
    />
  );
}
